// src/pages/SubCategoryPage.jsx

import React, { useEffect, useState } from "react";
import axios from "axios";
import Select from "react-select";
import DataTable from "react-data-table-component";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL } from "../../config";

const SubCategoryPage = () => {
  const [form, setForm] = useState({
    company_id: "",
    category_id: "", // main category
    name: "",
  });

  const [companies, setCompanies] = useState([]);
  const [mainCategories, setMainCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [filterText, setFilterText] = useState("");

  // --- Edit modal state ---
  const [showEdit, setShowEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({
    company_id: "",
    category_id: "",
    name: "",
  });

  /* ===========================================
   *  LOADERS
   * ===========================================
   */

  const loadCompanies = async () => {
    try {
      const res = await axios.get(API_BASE_URL + "/companies", {
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
      });

      const d = res.data;
      let arr = [];
      if (Array.isArray(d)) arr = d;
      else if (Array.isArray(d.data)) arr = d.data;
      else if (Array.isArray(d.companies)) arr = d.companies;

      setCompanies(arr || []);
    } catch (err) {
      console.error("Companies load error:", err);
      toast.error("Failed to load companies");
    }
  };

  // main categories (for dropdown)
  const loadMainCategories = async () => {
    try {
      const res = await axios.get(API_BASE_URL + "/categories", {
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
      });

      const d = res.data;
      let arr = [];
      if (Array.isArray(d)) arr = d;
      else if (Array.isArray(d.data)) arr = d.data;
      else if (Array.isArray(d.categories)) arr = d.categories;

      setMainCategories(arr || []);
    } catch (err) {
      console.error("Main categories load error:", err);
      toast.error("Failed to load main categories");
    }
  };

  // subcategory list
  const loadSubcategories = async () => {
    try {
      const res = await axios.get(API_BASE_URL + "/subcategories", {
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
      });

      const d = res.data;
      let arr = [];
      if (Array.isArray(d)) arr = d;
      else if (Array.isArray(d.data)) arr = d.data;
      else if (Array.isArray(d.subcategories)) arr = d.subcategories;

      setSubcategories(arr || []);
    } catch (err) {
      console.error("Subcategories load error:", err);
      toast.error("Failed to load subcategories");
    }
  };

  useEffect(() => {
    loadCompanies();
    loadMainCategories();
    loadSubcategories();
  }, []);

  /* ===========================================
   *  ADD SUBCATEGORY FORM
   * ===========================================
   */

  const handleInput = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await axios.post(API_BASE_URL + "/subcategories", form, {
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
      });

      toast.success("Sub Category added successfully!");
     
      setForm({
        company_id: "",
        category_id: "",
        name: "",
      });

      loadSubcategories();
    } catch (err) {
      if (err.response?.status === 422 && err.response.data.errors) {
        Object.values(err.response.data.errors).forEach((msg) =>
          toast.error(msg[0])
        );
      } else {
        console.error("Add subcategory error:", err);
        toast.error("Error adding subcategory");
      }
    }
  };

  /* ===========================================
   *  DELETE SUBCATEGORY
   * ===========================================
   */

  const deleteSubcategory = async (id) => {
    if (!window.confirm("Delete this sub category?")) return;

    try {
      await axios.delete(API_BASE_URL + `/subcategories/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
      });

      toast.success("Sub Category deleted successfully!");
        
      loadSubcategories();
    } catch (err) {
      console.error("Delete subcategory error:", err);
      toast.error("Error deleting subcategory");
    }
  };

  /* ===========================================
   *  EDIT SUBCATEGORY (MODAL)
   * ===========================================
   */

  const openEditModal = async (row) => {
    try {
      const res = await axios.get(
        API_BASE_URL + `/subcategories/${row.assets_subcategory_id}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
        }
      );

      const d = res.data.data;

      // expect d: { id, company_id, main_category_id or category_id, category_name }
      setEditId(d.id || d.assets_subcategory_id);

      setEditForm({
        company_id: d.company_id,
        category_id: d.main_category_id || d.category_id,
        name: d.category_name,
      });

      setShowEdit(true);
    } catch (err) {
      console.error("Load subcategory error:", err);
      toast.error("Failed to load subcategory");
    }
  };

  const handleEditInput = (e) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    try {
      await axios.put(API_BASE_URL + `/subcategories/${editId}`, editForm, {
        headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
      });

      toast.success("Sub Category updated successfully!");
     

      setShowEdit(false);
      loadSubcategories();
    } catch (err) {
      if (err.response?.status === 422 && err.response.data.errors) {
        Object.values(err.response.data.errors).forEach((msg) =>
          toast.error(msg[0])
        );
      } else {
        console.error("Update subcategory error:", err);
        toast.error("Error updating subcategory");
      }
    }
  };

  /* ===========================================
   *  DATATABLE
   * ===========================================
   */

  const columns = [
    {
      name: "Action",
      width: "120px",
      cell: (row) => (
        <>
          <button
        className="border-0 bg-transparent me-2"
        title="Edit"
        onClick={() => openEditModal(row)}
        style={{ cursor: "pointer" }}
      >
        <i className="fa fa-pencil text-warning" style={{ fontSize: "18px" }}></i>
          </button>
          {/* <button
            className="btn btn-danger btn-sm"
            onClick={() => deleteSubcategory(row.assets_subcategory_id)}
          >
            <i className="fa fa-trash" />
          </button> */}
        </>
      ),
    },
    {
      name: "Sub Category",
      selector: (row) => row.category_name,
      sortable: true,
    },
    {
      name: "Main Category",
      selector: (row) => row.main_category,
      sortable: true,
    },
  ];

  const filteredItems = (subcategories || []).filter((item) => {
    const txt = filterText.toLowerCase();
    return (
      item.category_name?.toLowerCase().includes(txt) ||
      item.main_category?.toLowerCase().includes(txt)
    );
  });

  /* ===========================================
   *  RENDER
   * ===========================================
   */

  return (
    <div className="container mt-4">
     <ToastContainer position="bottom-right" />


      <div className="row">
        {/* LEFT FORM */}
        <div className="col-md-4">
          <div className="card shadow-sm">
            <div className="card-header">
              <strong>Add New Sub Category</strong>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                {/* Company */}
                <div className="mb-3">
                  <label className="form-label">
                    Company 
                  </label>
                  <Select  classNames={{
    control: () => "required-select required-tooltip"
  }}
                    options={(companies || []).map((c) =>
                      c.label && c.value
                        ? c
                        : {
                            label: c.name,
                            value: c.company_id,
                          }
                    )}
                    value={
                      (companies || [])
                        .map((c) =>
                          c.label && c.value
                            ? c
                            : {
                                label: c.name,
                                value: c.company_id,
                              }
                        )
                        .find((opt) => opt.value === form.company_id) || null
                    }
                    onChange={(selected) =>
                      setForm((prev) => ({
                        ...prev,
                        company_id: selected ? selected.value : "",
                      }))
                    }
                    placeholder="Select company..."
                    className="form-control p-0"
                  />
                </div>

                {/* Main Category */}
                <div className="mb-3">
                  <label className="form-label">
                    Main Category 
                  </label>
                  <Select  classNames={{
    control: () => "required-select required-tooltip"
  }}
                    options={(mainCategories || []).map((cat) => ({
                      label: cat.category_name,
                      value: cat.assets_category_id,
                    }))}
                    value={
                      (mainCategories || [])
                        .map((cat) => ({
                          label: cat.category_name,
                          value: cat.assets_category_id,
                        }))
                        .find((opt) => opt.value === form.category_id) || null
                    }
                    onChange={(selected) =>
                      setForm((prev) => ({
                        ...prev,
                        category_id: selected ? selected.value : "",
                      }))
                    }
                    placeholder="Select main category..."
                    className="form-control p-0"
                  />
                </div>

                {/* Sub Category Name */}
                <div className="mb-3">
                  <label className="form-label">
                    Sub Category Name 
                  </label>
                  <input
                    type="text"
                    name="name"
                    className="form-control required-input"
                    value={form.name}
                    onChange={handleInput}
                  />
                </div>

                <button type="submit" className="btn btn-primary w-100">
                  Save Sub Category
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* RIGHT TABLE */}
        <div className="col-md-8">
          <div className="card shadow-sm">
            <div className="card-header d-flex justify-content-between align-items-center">
              <strong>Sub Category List</strong>
              <input
                type="text"
                className="form-control form-control-sm d-inline-block"
                style={{ width: "200px" }}
                placeholder="Search..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
              />
            </div>
            <div className="card-body">
              <DataTable
                columns={columns}
                data={filteredItems}
                pagination
                highlightOnHover
                striped
                persistTableHead
              />
            </div>
          </div>
        </div>
      </div>

      {/* EDIT MODAL */}
      {showEdit && (
        <div
          className="modal fade show"
          style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <form onSubmit={handleUpdate}>
                <div className="modal-header">
                  <h5 className="modal-title">Edit Sub Category</h5>
                   <button
  type="button"
  onClick={() => setShowEdit(false)}
  style={{
    border: "none",
    background: "transparent",
    fontSize: "22px",
    cursor: "pointer",
  }}
>
  ✕
</button>
                </div>
                <div className="modal-body">
                  {/* Company */}
                  <div className="mb-3">
                    <label className="form-label">Company</label>
                    <Select
                      options={(companies || []).map((c) =>
                        c.label && c.value
                          ? c
                          : {
                              label: c.name,
                              value: c.company_id,
                            }
                      )}
                      value={
                        (companies || [])
                          .map((c) =>
                            c.label && c.value
                              ? c
                              : {
                                  label: c.name,
                                  value: c.company_id,
                                }
                          )
                          .find((opt) => opt.value === editForm.company_id) ||
                        null
                      }
                      onChange={(selected) =>
                        setEditForm((prev) => ({
                          ...prev,
                          company_id: selected ? selected.value : "",
                        }))
                      }
                      placeholder="Select company..."
                      className="form-control p-0"
                    />
                  </div>

                  {/* Main Category */}
                  <div className="mb-3">
                    <label className="form-label">Main Category</label>
                    <Select
                      options={(mainCategories || []).map((cat) => ({
                        label: cat.category_name,
                        value: cat.assets_category_id,
                      }))}
                      value={
                        (mainCategories || [])
                          .map((cat) => ({
                            label: cat.category_name,
                            value: cat.assets_category_id,
                          }))
                          .find((opt) => opt.value === editForm.category_id) ||
                        null
                      }
                      onChange={(selected) =>
                        setEditForm((prev) => ({
                          ...prev,
                          category_id: selected ? selected.value : "",
                        }))
                      }
                      placeholder="Select main category..."
                      className="form-control p-0"
                    />
                  </div>

                  {/* Sub Category Name */}
                  <div className="mb-3">
                    <label className="form-label">Sub Category Name</label>
                    <input
                      type="text"
                      name="name"
                      className="form-control required-input"
                      value={editForm.name}
                      onChange={handleEditInput}
                    />
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowEdit(false)}
                  >
                    Close
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Update
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubCategoryPage;
